/**
 * Session service for In Session mode
 * Manages session creation, joining, and ending
 */

import { db } from '../firebase';
import { getEnergyTypeForLiveEvent } from '../constants/energyTypes';
import type { EnergyType } from '../types/season1';
import { updatePlayerWorkStats } from './workStatsTracking';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
  runTransaction
} from 'firebase/firestore';
import { notifyStudentsOfLiveEvent } from './liveEventStudentAlerts';
import { debug, debugError } from './inSessionDebug';
import { mstLiveLog, mstLiveError } from './mstLiveDebug';
import { isUserAdmin } from './roleManagement';
import { ensurePlayerStatsIfMissing, finalizeSessionStats, LIVE_EVENT_PP_BASE_PER_ELIMINATION, LIVE_EVENT_PP_PER_PARTICIPATION_POINT } from './inSessionStatsService';
import { recordLiveEventParticipationForPlayer } from './weeklyGoalsService';

export interface SessionPlayer {
  userId: string;
  displayName: string;
  /** Player classroom for Universal Event reporting/aggregation. */
  classId?: string | null;
  photoURL?: string;
  level: number;
  /** Total Power Level (PL) from profile / artifacts — optional for older session rows */
  powerLevel?: number | null;
  powerPoints: number;
  participationCount: number;
  movesEarned: number;
  eliminated?: boolean;
  /** Set when another player eliminated this user (BR / in-session combat). */
  eliminatedBy?: string;
  hp?: number;
  maxHp?: number;
  shield?: number;
  maxShield?: number;
  /** Consecutive successful participation events (quiz correct, sprint reward, host PP) — mirrors stats */
  successStreak?: number;
  flowStateActive?: boolean;
  flowStateActivatedAt?: number | null;
  /** Bumps when entering Flow State; clients use for one-shot entry animation */
  flowStateNonce?: number;
  flowState?: import('../types/liveEventFlowBoons').LiveEventFlowStateBoons;
  /** Wall-clock ms anchor for passive +1 movesEarned every 2 minutes (liveEventPassiveParticipation). */
  participationPassiveStartedAtMs?: number;
  /** Host / teacher participant row */
  isTeacher?: boolean;
  isReady?: boolean;
  participationMode?: 'online' | 'offline';
  liveEventStartingPP?: number;
}

export interface InSessionRoom {
  id: string;
  classId: string | null;
  classIds?: string[];
  inviteAllClasses?: boolean;
  eventType?: 'live_event' | 'universal_event';
  className: string;
  teacherId: string;
  hostUid: string; // UID of the host (admin who started session)
  status: 'live' | 'active' | 'ended'; // 'active' for backward compatibility
  mode: 'in_session';
  createdAt: any;
  startedAt?: any;
  endedAt?: any;
  players: SessionPlayer[];
  battleLog: string[];
  /** Host opens MST MKT so players can spend session PP on heal / shield / revive items. */
  mstMktOpen?: boolean;
  /** When true, non-host players cannot use Fight / skills until cleared. */
  liveEventFightNegated?: boolean;
  /** Goal setting mode: linked assessment for in-room goal forms. */
  goalSettingAssessmentId?: string;
  goalSettingPrompt?: string | null;
}

/**
 * Check if user is Yondaime (global host)
 */
export function isGlobalHost(uid: string, email?: string, displayName?: string): boolean {
  // Email only: display names are user-editable, so they must never grant host powers.
  void uid;
  void displayName;
  return email === 'edm21179@gmail.com';
}

/**
 * Check if user can host sessions for a class
 */
export async function canHostSession(
  userId: string,
  classId: string,
  userEmail?: string,
  userDisplayName?: string
): Promise<boolean> {
  // Check if user is admin
  const isAdmin = await isUserAdmin(userId, userEmail);
  if (isAdmin) return true;
  
  // Check if user is Yondaime (global host)
  if (isGlobalHost(userId, userEmail, userDisplayName)) return true;
  
  // TODO: Check if user is class admin (if class admin system exists)
  // For now, only admins and Yondaime can host
  
  return false;
}

/**
 * Create a new session
 */
export async function createSession(
  classId: string,
  className: string,
  hostUid: string,
  hostDisplayName?: string
): Promise<string | null> {
  try {
    // Check for existing active session
    const existingSession = await getActiveSessionForClass(classId);
    if (existingSession) {
      debug('inSessionService', `Active session already exists for class ${classId}: ${existingSession.id}`);
      return existingSession.id;
    }
    
    const hostName =
      typeof hostDisplayName === 'string' && hostDisplayName.trim()
        ? hostDisplayName.trim()
        : 'Host';

    // Seed host as a participant so they can fight even if not on the class roster
    const hostPlayer: SessionPlayer = {
      userId: hostUid || '',
      displayName: hostName,
      level: 1,
      isTeacher: true,
      isReady: true,
      movesEarned: 5,
      participationCount: 0,
      powerPoints: 0,
      participationMode: 'online',
      liveEventStartingPP: 0,
      hp: 100,
      maxHp: 100,
      shield: 100,
      maxShield: 100,
    };

    // CRITICAL: Ensure all required fields are defined (no undefined values)
    const sessionData = {
      classId: classId || '',
      className: className || '',
      teacherId: hostUid || '', // Keep for backward compatibility
      hostUid: hostUid || '',
      status: 'live' as const, // Use 'live' consistently
      mode: 'in_session' as const,
      /** Default Live Event mode — Class Flow (sprints / participation). */
      liveEventMode: 'class_flow' as const,
      goalLinkingEnabled: true,
      energyTypeAwarded: 'physical',
      players: [hostPlayer] as SessionPlayer[],
      battleLog: [
        `🎉 Live Event is now active for ${className || 'this class'}! Join the battle in the arena.`,
        `👑 ${hostName} joined as host (can participate in battle).`,
        '🏃 Mode: Class Flow — use the Sprint panel for timed goals and participation rewards.',
        '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
        '📋 How to earn PP this event:',
        `  • +${LIVE_EVENT_PP_BASE_PER_ELIMINATION} PP for every player you eliminate (plus their vault PP)`,
        '  • PP for each correct quiz answer (more for faster answers)',
        `  • +${LIVE_EVENT_PP_PER_PARTICIPATION_POINT} PP for every Participation Point the host gives you`,
        '  • If you are eliminated, quiz and elimination PP from this event are halved at the end',
        '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      ] as string[],
      createdAt: serverTimestamp(),
      startedAt: serverTimestamp()
    };

    // Validate required fields
    if (!sessionData.classId || !sessionData.hostUid) {
      debugError('inSessionService', 'Cannot create session: missing required fields', {
        classId: sessionData.classId,
        hostUid: sessionData.hostUid
      });
      return null;
    }
    
    const sessionRef = doc(collection(db, 'inSessionRooms'));
    await setDoc(sessionRef, sessionData);

    try {
      const { upsertLiveEventSessionStub } = await import('./liveEventHistoryService');
      await upsertLiveEventSessionStub(sessionRef.id, sessionData as unknown as Record<string, unknown>);
    } catch (stubErr) {
      debugError('inSessionService', 'Live Event history stub failed (non-fatal)', stubErr);
    }

    try {
      const classroomSnap = await getDoc(doc(db, 'classrooms', classId));
      const enrolled = classroomSnap.exists()
        ? ((classroomSnap.data().students as string[]) || []).filter(Boolean)
        : [];
      await notifyStudentsOfLiveEvent({
        studentIds: enrolled.filter((id) => id !== hostUid),
        sessionId: sessionRef.id,
        className,
        classId,
      });
    } catch (notifyErr) {
      debugError('inSessionService', 'Live event student alerts failed (session still created)', notifyErr);
    }
    
    debug('inSessionService', `Session created: ${sessionRef.id} for class ${classId}`);
    
    return sessionRef.id;
  } catch (error) {
    debugError('inSessionService', `Error creating session for class ${classId}`, error);
    return null;
  }
}

/**
 * Get active session for a class
 */
export async function getActiveSessionForClass(classId: string): Promise<InSessionRoom | null> {
  try {
    const sessionsRef = collection(db, 'inSessionRooms');
    const q = query(
      sessionsRef,
      where('classId', '==', classId),
      where('status', '==', 'live')
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    // Get the most recent session
    const sessions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as InSessionRoom));
    
    sessions.sort((a, b) => {
      const aTime = a.startedAt?.toMillis?.() || 0;
      const bTime = b.startedAt?.toMillis?.() || 0;
      return bTime - aTime;
    });
    
    return sessions[0] || null;
  } catch (error) {
    debugError('inSessionService', `Error getting active session for class ${classId}`, error);
    return null;
  }
}

/**
 * Join a session (idempotent - safe to call multiple times)
 * NOW TRANSACTION-SAFE: Uses Firestore transaction to prevent race conditions
 * Returns: { success: boolean, error?: string, isNewPlayer?: boolean }
 */
export async function joinSession(
  sessionId: string,
  player: SessionPlayer,
  options?: { participationMode?: 'online' | 'offline' }
): Promise<{ success: boolean; error?: string; isNewPlayer?: boolean }> {
  const DEBUG_JOIN = process.env.REACT_APP_DEBUG_LIVE_EVENTS === 'true' || 
                     process.env.REACT_APP_DEBUG === 'true';
  
  if (DEBUG_JOIN) {
    debug('inSessionService', `🔵 JOIN ATTEMPT: ${player.userId} joining session ${sessionId}`, {
      playerName: player.displayName,
      playerLevel: player.level,
      playerPP: player.powerPoints
    });
  }
  
  // Declare refs outside try block so they're accessible in catch block
  const sessionRef = doc(db, 'inSessionRooms', sessionId);
  const playerPresenceRef = doc(db, 'inSessionRooms', sessionId, 'players', player.userId);
  
  try {
    
    // Quick check: If player is already in session, return success immediately (idempotent)
    // This prevents unnecessary transaction conflicts
    try {
      const quickCheck = await getDoc(sessionRef);
      if (quickCheck.exists()) {
        const quickData = quickCheck.data() as InSessionRoom;
        const alreadyInSession = quickData.players?.some(p => p.userId === player.userId) || false;
        if (alreadyInSession) {
          if (DEBUG_JOIN) {
            debug('inSessionService', `✅ Player ${player.userId} already in session, skipping join`);
          }
          // Still update presence to show they're connected
          try {
            await updateDoc(playerPresenceRef, {
              connected: true,
              lastSeenAt: serverTimestamp()
            });
          } catch (presenceError) {
            // If presence doc doesn't exist, create it
            try {
              await setDoc(playerPresenceRef, {
                connected: true,
                lastSeenAt: serverTimestamp(),
                joinedAt: serverTimestamp()
              });
            } catch (setError) {
              // Ignore - presence is optional
            }
          }
          try {
            await ensurePlayerStatsIfMissing(sessionId, player.userId, {
              playerName: player.displayName,
              startingPP: player.powerPoints
            });
          } catch (_) {
            // stats heal is best-effort
          }
          return { success: true, isNewPlayer: false as const };
        }
      }
    } catch (quickCheckError) {
      // Continue with full transaction if quick check fails
      if (DEBUG_JOIN) {
        debug('inSessionService', 'Quick check failed, proceeding with transaction', quickCheckError);
      }
    }
    
    // Use transaction to ensure atomic join
    // CRITICAL: Firestore transactions require ALL reads before ANY writes
    // Firestore will automatically retry on failed-precondition errors
    const result = await runTransaction(db, async (transaction) => {
      // PHASE 1: ALL READS FIRST (Firestore requirement)
      // Read session document
      const sessionDoc = await transaction.get(sessionRef);
      
      if (!sessionDoc.exists()) {
        if (DEBUG_JOIN) {
          debugError('inSessionService', `❌ JOIN FAILED: Session ${sessionId} does not exist`);
        }
        throw new Error(`Session ${sessionId} does not exist`);
      }
      
      const sessionData = sessionDoc.data() as InSessionRoom;
      
      // Check session status - allow 'live' and 'active' for backward compatibility
      if (sessionData.status !== 'live' && sessionData.status !== 'active') {
        if (DEBUG_JOIN) {
          debugError('inSessionService', `❌ JOIN FAILED: Session ${sessionId} is not live (status: ${sessionData.status})`);
        }
        throw new Error(`Session is not active. Current status: ${sessionData.status}`);
      }
      
      // Read presence document BEFORE any writes
      const presenceDoc = await transaction.get(playerPresenceRef);
      
      // Check if player already exists
      const existingPlayerIndex = sessionData.players.findIndex(p => p.userId === player.userId);
      const isNewPlayer = existingPlayerIndex === -1;
      
      // CRITICAL: Validate player data before adding to array
      if (!player.userId || typeof player.userId !== 'string') {
        debugError('inSessionService', 'Invalid player data: userId is required', { player });
        throw new Error('Player userId is required and must be a string');
      }
      const resolvedName =
        typeof player.displayName === 'string' && player.displayName.trim()
          ? player.displayName.trim()
          : 'Player';
      const participationMode = options?.participationMode || player.participationMode || 'online';
      const startingPP = Math.max(0, Math.floor(Number(player.powerPoints) || 0));
      player = {
        ...player,
        displayName: resolvedName,
        participationMode,
        liveEventStartingPP:
          player.liveEventStartingPP != null
            ? Math.max(0, Math.floor(Number(player.liveEventStartingPP) || 0))
            : startingPP,
      };

      // PHASE 2: ALL WRITES AFTER READS
      // Update or add player
      const updatedPlayers = [...sessionData.players];
      if (isNewPlayer) {
        updatedPlayers.push(player);
        if (DEBUG_JOIN) {
          debug('inSessionService', `✅ NEW PLAYER: Adding ${player.displayName} to session`);
        }
      } else {
        // Idempotent rejoin: refresh identity only — never wipe combat/economy progress
        const existing = updatedPlayers[existingPlayerIndex];
        updatedPlayers[existingPlayerIndex] = {
          ...existing,
          displayName: player.displayName || existing.displayName,
          photoURL: player.photoURL !== undefined ? player.photoURL : existing.photoURL,
          userId: existing.userId || player.userId,
        };
        if (DEBUG_JOIN) {
          debug('inSessionService', `🔄 REJOIN: Updating existing player ${player.displayName}`);
        }
      }
      
      // Update battle log only for new players
      // CRITICAL: Validate battle log message is not undefined
      const joinMessage = `👋 ${player.displayName || 'Player'} joined the session!`;
      if (!joinMessage || typeof joinMessage !== 'string') {
        debugError('inSessionService', 'Invalid join message', { joinMessage, player });
        throw new Error('Join message must be a valid string');
      }
      const updatedLog = isNewPlayer 
        ? [...(sessionData.battleLog || []), joinMessage]
        : sessionData.battleLog || [];
      
      // Update session document
      transaction.update(sessionRef, {
        players: updatedPlayers,
        battleLog: updatedLog,
        updatedAt: serverTimestamp()
      });
      
      // Update presence document (we already read it above)
      if (!presenceDoc.exists()) {
        transaction.set(playerPresenceRef, {
          connected: true,
          lastSeenAt: serverTimestamp(),
          joinedAt: serverTimestamp()
        });
        if (DEBUG_JOIN) {
          debug('inSessionService', `📝 Created presence doc for ${player.userId}`);
        }
      } else {
        transaction.update(playerPresenceRef, {
          connected: true,
          lastSeenAt: serverTimestamp()
        });
        if (DEBUG_JOIN) {
          debug('inSessionService', `🔄 Updated presence doc for ${player.userId}`);
        }
      }
      
      return { isNewPlayer, playerCount: updatedPlayers.length };
    });
    
    // Ensure stats subdoc exists (new join, rejoin after failed init, or race recovery)
    try {
      await ensurePlayerStatsIfMissing(sessionId, player.userId, {
        playerName: player.displayName,
        startingPP: player.powerPoints
      });
      if (DEBUG_JOIN) {
        debug('inSessionService', `📊 Ensured stats doc for ${player.userId} (isNewPlayer=${result.isNewPlayer})`);
      }
    } catch (statsError) {
      debugError('inSessionService', `Error ensuring stats for ${player.userId}`, statsError);
    }
    
    if (DEBUG_JOIN) {
      debug('inSessionService', `✅ JOIN SUCCESS: ${player.displayName} joined session ${sessionId}`, {
        isNewPlayer: result.isNewPlayer,
        playerCount: result.playerCount
      });
    }
    mstLiveLog(result.isNewPlayer ? 'JOIN' : 'RECONNECT', result.isNewPlayer ? 'Player joined' : 'Player rejoined', {
      eventId: sessionId,
      userId: player.userId,
      playerCount: result.playerCount,
      isNewPlayer: result.isNewPlayer,
    });

    if (result.isNewPlayer) {
      try {
        const sd = await getDoc(sessionRef);
        const mode =
          sd.exists() && typeof (sd.data() as { liveEventMode?: string }).liveEventMode === 'string'
            ? (sd.data() as { liveEventMode: string }).liveEventMode
            : undefined;
        const neutral = sd.exists()
          ? ((sd.data() as { neutralFlowEnergyType?: EnergyType }).neutralFlowEnergyType as
              | EnergyType
              | undefined)
          : undefined;
        const isClassFlowSprintOnly =
          !mode || mode === 'class_flow' || mode === 'neutral_flow';
        if (!isClassFlowSprintOnly) {
          const energy = getEnergyTypeForLiveEvent(mode, neutral);
          void updatePlayerWorkStats({
            userId: player.userId,
            energyType: energy,
            attemptedIncrement: 1,
            classId:
              typeof (sd.data() as { classId?: string })?.classId === 'string'
                ? (sd.data() as { classId: string }).classId
                : undefined,
            playerName: player.displayName,
            source: 'live_event',
            sourceId: sessionId,
          });
        }
      } catch (wsErr) {
        console.warn('[inSessionService] workStats join', wsErr);
      }
    }

    return { success: true, isNewPlayer: result.isNewPlayer };
  } catch (error: any) {
    const errorMessage = error?.message || 'Unknown error occurred';
    const errorCode = error?.code || 'unknown';
    
    debugError('inSessionService', `❌ JOIN ERROR: Error joining session ${sessionId}`, error);
    console.error('[InSession] Join error details:', {
      sessionId,
      playerId: player.userId,
      playerName: player.displayName,
      errorMessage,
      errorCode,
      errorStack: error?.stack
    });
    
    // If it's a failed-precondition error, the transaction was retried but still failed
    // This usually means the document is being updated too frequently
    // Check if player actually got added despite the error
    if (errorCode === 'failed-precondition') {
      try {
        const checkDoc = await getDoc(sessionRef);
        if (checkDoc.exists()) {
          const checkData = checkDoc.data() as InSessionRoom;
          const playerInSession = checkData.players?.some(p => p.userId === player.userId) || false;
          if (playerInSession) {
            console.log('[InSession] Player was added despite transaction error - join succeeded');
            // Update presence anyway
            try {
              await updateDoc(playerPresenceRef, {
                connected: true,
                lastSeenAt: serverTimestamp()
              });
            } catch (presenceError) {
              // Ignore presence errors
            }
            try {
              await ensurePlayerStatsIfMissing(sessionId, player.userId, {
                playerName: player.displayName,
                startingPP: player.powerPoints
              });
            } catch (_) {}
            return { success: true, isNewPlayer: false };
          }
        }
      } catch (checkError) {
        // Ignore check errors
      }
    }
    
    mstLiveError('JOIN', 'Join failed', error, {
      eventId: sessionId,
      userId: player.userId,
      errorCode,
    });

    // Return detailed error information
    return { 
      success: false, 
      error: errorMessage 
    };
  }
}

const END_SESSION_LOCK_MS = 3 * 60 * 1000;

/**
 * End a session
 */
export async function endSession(sessionId: string, hostUid: string, userEmail?: string): Promise<boolean> {
  try {
    const sessionRef = doc(db, 'inSessionRooms', sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (!sessionDoc.exists()) {
      debugError('inSessionService', `Session ${sessionId} does not exist`);
      return false;
    }
    
    const sessionData = sessionDoc.data() as InSessionRoom;

    // Soft-gate: double End must not re-finalize rewards / placements
    if (sessionData.status === 'ended') {
      mstLiveLog('PLACEMENT', 'endSession no-op — already ended', {
        eventId: sessionId,
        userId: hostUid,
      });
      return true;
    }
    
    // Host = hostUid or legacy teacherId (InSessionCreate historically omitted hostUid)
    const isHost =
      sessionData.hostUid === hostUid ||
      sessionData.teacherId === hostUid;
    
    // Check if user is an admin (allow admins to end any session)
    const isAdmin = await isUserAdmin(hostUid, userEmail);
    
    // Check if user is global host (Yondaime)
    const isGlobal = isGlobalHost(hostUid, userEmail);
    
    // Allow if user is host, admin, or global host
    if (!isHost && !isAdmin && !isGlobal) {
      debugError('inSessionService', `User ${hostUid} is not authorized to end session ${sessionId} (not host, admin, or global host)`);
      return false;
    }
    
    // Claim the end atomically: finalization pays every player, so a double click or a second host
    // ending the same room while the first finalize is still running must not pay twice.
    const endLock = await runTransaction(db, async (tx) => {
      const snap = await tx.get(sessionRef);
      if (!snap.exists()) return 'missing' as const;
      const d = snap.data() as { status?: string; endingLockMs?: number };
      if (d.status === 'ended') return 'ended' as const;
      const lockMs = Number(d.endingLockMs) || 0;
      if (lockMs > 0 && Date.now() - lockMs < END_SESSION_LOCK_MS) return 'busy' as const;
      tx.update(sessionRef, { endingLockMs: Date.now(), endingLockBy: hostUid });
      return 'acquired' as const;
    });
    if (endLock === 'missing') return false;
    if (endLock !== 'acquired') {
      mstLiveLog('PLACEMENT', `endSession no-op — ${endLock === 'ended' ? 'already ended' : 'another end is in progress'}`, {
        eventId: sessionId,
        userId: hostUid,
      });
      return true;
    }

    // Finalize session stats before ending
    const playerIds = sessionData.players.map((p: SessionPlayer) => p.userId);
    const summary = await finalizeSessionStats(sessionId, playerIds);

    const endedLogEntry = '🏁 Session ended by host. Thanks for playing!';
    const updatedBattleLog = [...(sessionData.battleLog || []), endedLogEntry];

    // Finalize session and update battle log so all players see it ended
    await updateDoc(sessionRef, {
      status: 'ended',
      endedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      battleLog: updatedBattleLog
    });

    mstLiveLog('PLACEMENT', 'Session ended', {
      eventId: sessionId,
      userId: hostUid,
      playerCount: playerIds.length,
      summaryGenerated: !!summary,
    });
    
    debug('inSessionService', `Session ${sessionId} ended by ${hostUid} (${isHost ? 'host' : isAdmin ? 'admin' : 'global host'})`, {
      summaryGenerated: !!summary,
      players: playerIds.length
    });
    
    return true;
  } catch (error) {
    debugError('inSessionService', `Error ending session ${sessionId}`, error);
    return false;
  }
}

/**
 * Leave a session (remove current user from players; does not end the session)
 * Any player can leave. Only admins/hosts can end the session.
 */
export async function leaveSession(sessionId: string, userId: string, displayName?: string): Promise<boolean> {
  try {
    const sessionRef = doc(db, 'inSessionRooms', sessionId);
    const sessionDoc = await getDoc(sessionRef);

    if (!sessionDoc.exists()) {
      debugError('inSessionService', `Session ${sessionId} does not exist`);
      return false;
    }

    const sessionData = sessionDoc.data() as InSessionRoom;
    if (sessionData.status !== 'live' && sessionData.status !== 'active') {
      debug('inSessionService', `Session ${sessionId} is not live (status: ${sessionData.status}), treat as left`);
      return true;
    }

    const updatedPlayers = (sessionData.players || []).filter(p => p.userId !== userId);
    const leftMessage = `👋 ${displayName || 'A player'} left the session.`;
    const updatedLog = [...(sessionData.battleLog || []), leftMessage];

    await updateDoc(sessionRef, {
      players: updatedPlayers,
      battleLog: updatedLog,
      updatedAt: serverTimestamp()
    });

    debug('inSessionService', `User ${userId} left session ${sessionId}`);
    return true;
  } catch (error) {
    debugError('inSessionService', `Error leaving session ${sessionId}`, error);
    return false;
  }
}

/**
 * Subscribe to session updates
 * CRITICAL: Ensure cleanup is called to prevent duplicate subscriptions
 */
export function subscribeToSession(
  sessionId: string,
  callback: (session: InSessionRoom | null) => void
): Unsubscribe {
  const DEBUG_SESSION = process.env.REACT_APP_DEBUG_SESSION === 'true';
  
  if (DEBUG_SESSION) {
    console.log(`[Listener] [inSessionService] Subscribing to session ${sessionId}`);
  }
  
  const sessionRef = doc(db, 'inSessionRooms', sessionId);
  
  return onSnapshot(
    sessionRef,
    (doc) => {
      if (doc.exists()) {
        const session = {
          id: doc.id,
          ...doc.data()
        } as InSessionRoom;
        
        if (DEBUG_SESSION) {
          console.log(`[Listener] [inSessionService] Session update: ${sessionId}`, {
            status: session.status,
            playersCount: session.players.length,
            battleLogLength: session.battleLog?.length || 0
          });
        }
        
        callback(session);
      } else {
        if (DEBUG_SESSION) {
          console.log(`[Listener] [inSessionService] Session ${sessionId} does not exist`);
        }
        callback(null);
      }
    },
    (error) => {
      // Suppress Firestore internal assertion errors (known Firefox issue)
      if (error instanceof Error && 
          (error.message?.includes('INTERNAL ASSERTION FAILED') || 
           error.message?.includes('Unexpected state') ||
           error.message?.includes('400'))) {
        console.warn(`[inSessionService] Firestore error suppressed for session ${sessionId}:`, error.message);
        // Don't call callback(null) on these errors - keep existing session state
        return;
      }
      debugError('inSessionService', 'Error in session subscription', error);
      callback(null);
    }
  );
}

/**
 * Get session by ID
 */
export async function getSession(sessionId: string): Promise<InSessionRoom | null> {
  try {
    const sessionRef = doc(db, 'inSessionRooms', sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (!sessionDoc.exists()) {
      return null;
    }
    
    return {
      id: sessionDoc.id,
      ...sessionDoc.data()
    } as InSessionRoom;
  } catch (error) {
    debugError('inSessionService', `Error getting session ${sessionId}`, error);
    return null;
  }
}



